import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config.js";
import { ExternalLink } from 'lucide-react';

const SourceCard = ({ msg, chatMessageId }) => {
    const [imageError, setImageError] = useState(false);
    const [signedUrls, setSignedUrls] = useState({ thumbnail: null });
    const [loading, setLoading] = useState(false);

    const handleImageError = () => {
        setImageError(true);
    };

    const fetchSignedUrl = async (blobPath) => {
        try {
            const response = await axios.get(`${config.serviceUrl}/get-signed-url`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("token"),
                },
                params: {
                    blob_path: blobPath,
                    chat_message_id: chatMessageId
                },
            });
            return response.data.signed_url;
        } catch (error) {
            console.error("Error fetching signed URL for", blobPath, error);
            return null;
        }
    };

    useEffect(() => {
        if (!msg) return;

        const fetchThumbnailUrl = async () => {
            let thumbnailUrl = null;

            const baseMediaUrl = config.baseMediaUrl; 
            const cleanThumbnailPath = msg.thumbnail_path.replace(baseMediaUrl, "");

            if (msg.type === "document" || msg.type === "webpage" || msg.type === "youtube") {
                thumbnailUrl = await fetchSignedUrl(cleanThumbnailPath);
            }

            setSignedUrls((prev) => ({ ...prev, thumbnail: thumbnailUrl }));
        };

        fetchThumbnailUrl();
    }, [msg]);

    const handleDocumentClick = async () => {
        if (msg.type === "document" && msg.filename) {
            try {
                setLoading(true);

                const signedUrl = await fetchSignedUrl(msg.file_path);
                if (signedUrl) {
                    window.open(signedUrl, "_blank", "noopener,noreferrer");
                }
            } catch (error) {
                console.error("Error fetching document URL:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 m-2 p-2 mr-3">
            <div className="crop">
                {/* Show loading spinner in place of the image */}
                {!signedUrls.thumbnail && !imageError && (
                    <div className="loading-placeholder flex justify-center items-center">
                        <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                )}

                {!imageError && msg.type === "document" && signedUrls.thumbnail && (
                    <img
                     
                    src={signedUrls.thumbnail}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-t-lg"
                        alt={msg.filename}
                        onError={handleImageError}
                    />
                )}
                {!imageError && msg.type === "youtube" && (
                    <img
                        src={`https://img.youtube.com/vi/${msg.url.split("v=")[1]}/maxresdefault.jpg`}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-t-lg"
                        alt={msg.filename}
                        onError={handleImageError}
                    />
                )}
                {!imageError && msg.type === "webpage" && signedUrls.thumbnail && (
                    <img
                        src={signedUrls.thumbnail}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-t-lg"
                        alt={msg.filename}
                        onError={handleImageError}
                    />
                )}
                {imageError && <p className="text-gray-500 dark:text-gray-400 p-2">Image Unavailable</p>}
            </div>
            <div className="p-3 flex flex-col">
                {msg.type === "document" && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-auto break-words">
                        {msg.filename} <br />
                        Page Number(s): {msg.pages.join(", ")}
                    </p>
                )}
                {msg.type === "youtube" && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-auto break-words">
                        {msg.vid_name} <br />
                        Timestamp(s):{" "}
                        {msg.timestamps.map((timestamp, index) => (
                            <span key={index}>
                                {timestamp} ({Math.round(msg.durations[index])} sec){" "}
                                {index < msg.timestamps.length - 1 ? " | " : ""}
                            </span>
                        ))}
                    </p>
                )}
                {msg.type === "webpage" && <p className="text-sm text-gray-700 dark:text-gray-300">{msg.filename}</p>}


                {msg.type === "document" && (
                    <button
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors mt-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleDocumentClick}
                        disabled={loading} 
                    >
                        {loading ? "Loading..." : msg.type}{" "}
                        <ExternalLink className="w-4 h-4 inline-block ml-1" />
                    </button>
                )}

                {msg.type !== "document" && msg.url && (
                    <a
                        href={msg.url}
                        target="_blank"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors mt-auto text-center inline-block"
                        rel="noopener noreferrer"
                    >
                        {msg.type} <ExternalLink className="w-4 h-4 inline-block ml-1" />
                    </a>
                )}
            </div>
        </div>
    );
};
export default SourceCard;